import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api, query } from '../api/client'
import { useInfinite, useResource, useToast } from '../api/hooks'
import type {
  AccessEvent, CountResult, CreateDoorCodeResult, CursorPage, DoorCodeDetail, DoorCodeListItem,
  DoorListItem, EffectiveAccess, Group, ProbeResult, ScheduleRule,
} from '../api/types'
import { PageHeader } from '../components/Layout'
import {
  ALL_DAYS, ScheduleGrid, ScheduleRuleList, summarizeSchedule,
} from '../components/ScheduleGrid'
import {
  Alert, Card, ConfirmDialog, ErrorBox, Field, GroupChips, InfiniteFooter, MaskedCode, Spinner,
  Toast, daysUntil, describeError, formatDate, formatDateTime,
} from '../components/Ui'
import { de, fmt } from '../i18n/de'

// ---- Liste ------------------------------------------------------------------------------

export function DoorCodesPage() {
  const [search, setSearch] = useState('')
  const [groupID, setGroupID] = useState('')
  const [doorID, setDoorID] = useState('')
  const [status, setStatus] = useState('active')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [showCreate, setShowCreate] = useState(false)
  const { toast, show, clear } = useToast()

  const groups = useResource<Group[]>('/groups')
  const doors = useResource<DoorListItem[]>('/doors')

  const filter = query({ q: search, groupId: groupID, doorId: doorID, status })
  const list = useInfinite<DoorCodeListItem>(`/doorcodes${filter}`)
  const count = useResource<CountResult>(`/doorcodes/count${filter}`)

  const toggle = (id: string) => {
    setSelected(current => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const runBulk = async (operation: string, bulkGroupID?: string) => {
    try {
      const result = await api.post<{ succeeded: number; failed: number }>('/doorcodes/bulk', {
        doorCodeIDs: [...selected],
        operation,
        groupID: bulkGroupID ?? null,
      })
      show(fmt(de.doorcodes.bulkResult, { succeeded: result.succeeded, failed: result.failed }),
           result.failed > 0 ? 'warn' : 'ok')
      setSelected(new Set())
      list.reload()
      count.reload()
    } catch (error) {
      show(describeError(error), 'warn')
    }
  }

  return (
    <>
      <PageHeader
        title={de.doorcodes.title}
        subtitle={count.data ? fmt(de.doorcodes.hits, { count: count.data.count }) : undefined}
        actions={<button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          {de.doorcodes.create}
        </button>} />

      <div className="toolbar">
        <input type="text" placeholder={`🔍 ${de.doorcodes.searchPlaceholder}`} value={search}
               onChange={event => setSearch(event.target.value)} style={{ width: 240 }} />

        <select value={groupID} onChange={event => setGroupID(event.target.value)} style={{ width: 'auto' }}>
          <option value="">{de.doorcodes.filterGroup}: {de.app.all}</option>
          {(groups.data ?? []).map(group => (
            <option key={group.id} value={group.id}>{group.name}</option>
          ))}
        </select>

        <select value={status} onChange={event => setStatus(event.target.value)} style={{ width: 'auto' }}>
          <option value="active">{de.doorcodes.filterStatus}: {de.doorcodes.statusActive}</option>
          <option value="inactive">{de.doorcodes.statusInactive}</option>
          <option value="all">{de.app.all}</option>
        </select>

        <select value={doorID} onChange={event => setDoorID(event.target.value)} style={{ width: 'auto' }}>
          <option value="">{de.doorcodes.filterDoor}: {de.app.all}</option>
          {(doors.data ?? []).map(door => (
            <option key={door.id} value={door.id}>{door.name}</option>
          ))}
        </select>
      </div>

      {list.error && <ErrorBox error={list.error} onRetry={list.reload} />}

      <div className="card">
        <table>
          <thead>
            <tr>
              <th style={{ width: 28 }} />
              <th>{de.doorcodes.columnName}</th>
              <th>{de.doorcodes.columnCode}</th>
              <th>{de.doorcodes.columnGroups}</th>
              <th>{de.doorcodes.columnValidUntil}</th>
              <th style={{ width: 48 }}>{de.doorcodes.columnKeyTag}</th>
            </tr>
          </thead>
          <tbody>
            {list.items.map(code => {
              const days = daysUntil(code.endDate)
              const warning = code.groups.length === 0 && code.hiddenGroupCount === 0
                && code.groupScope === 'restricted'
                ? de.doorcodes.noGroupWarning
                : days !== null && days >= 0 && days <= 14
                  ? fmt(de.doorcodes.expiresInDays, { days })
                  : null

              return (
                <DoorCodeRow key={code.id} code={code} warning={warning}
                             selected={selected.has(code.id)}
                             onToggle={() => toggle(code.id)}
                             onCopy={() => show(de.app.copied)} />
              )
            })}

            {!list.loading && list.items.length === 0 && (
              <tr><td colSpan={6} className="faint sm">{de.app.noEntries}</td></tr>
            )}
          </tbody>
        </table>

        <InfiniteFooter hasMore={list.hasMore} loading={list.loading} onLoadMore={list.loadMore} />
      </div>

      {selected.size > 0 && (
        <div className="card" style={{ marginTop: 'var(--sp-3)' }}>
          <div className="card-b" style={{ display: 'flex', gap: 'var(--sp-2)',
                                           alignItems: 'center', flexWrap: 'wrap' }}>
            <strong className="sm">{fmt(de.doorcodes.bulkTitle, { count: selected.size })}</strong>

            <select id="bulk-group" style={{ width: 'auto' }} defaultValue="">
              <option value="">{de.doorcodes.filterGroup}…</option>
              {(groups.data ?? []).map(group => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
            </select>

            <button className="btn btn-ghost" onClick={() => void runBulk('addGroup', pickGroup())}>
              {de.doorcodes.bulkAddGroup}
            </button>
            <button className="btn btn-ghost" onClick={() => void runBulk('removeGroup', pickGroup())}>
              {de.doorcodes.bulkRemoveGroup}
            </button>
            <button className="btn btn-ghost" onClick={() => void runBulk('deactivate')}>
              {de.doorcodes.bulkDeactivate}
            </button>
            <button className="btn btn-ghost" onClick={() => void runBulk('activate')}>
              {de.doorcodes.bulkActivate}
            </button>
            <button className="btn btn-danger" onClick={() => void runBulk('delete')}>
              {de.doorcodes.bulkDelete}
            </button>
            <div className="spacer" />
            <button className="btn btn-ghost" onClick={() => setSelected(new Set())}>
              {de.app.cancel}
            </button>
          </div>
        </div>
      )}

      {showCreate && (
        <CreateDoorCodeDialog
          groups={groups.data ?? []}
          onClose={() => setShowCreate(false)}
          onCreated={() => { list.reload(); count.reload() }} />
      )}

      {toast && <Toast message={toast.message} kind={toast.kind} onDone={clear} />}
    </>
  )
}

function pickGroup(): string | undefined {
  const select = document.getElementById('bulk-group') as HTMLSelectElement | null
  return select?.value || undefined
}

function DoorCodeRow({ code, warning, selected, onToggle, onCopy }: {
  code: DoorCodeListItem
  warning: string | null
  selected: boolean
  onToggle: () => void
  onCopy: () => void
}) {
  const navigate = useNavigate()

  return (
    <>
      <tr className="clickable" onClick={() => navigate(`/doorcodes/${code.id}`)}>
        <td onClick={event => event.stopPropagation()}>
          <input type="checkbox" checked={selected} onChange={onToggle}
                 aria-label={`${code.displayName} auswählen`} />
        </td>
        <td>
          {code.displayName}
          {!code.isActive && <span className="faint xs"> · {de.app.inactive}</span>}
        </td>
        <td onClick={event => event.stopPropagation()}>
          <MaskedCode code={code.doorcode} onCopy={onCopy} />
        </td>
        <td><GroupChips groups={code.groups} hidden={code.hiddenGroupCount} /></td>
        <td className="mono">{formatDate(code.endDate)}</td>
        <td>
          {code.keyTagAssigned
            ? <span title={`${de.doorcodes.keyTag} ${code.keyTagMasked} — ${de.doorcodes.keyTagAppOnly}`}>🔑</span>
            : <span className="faint">–</span>}
        </td>
      </tr>
      {warning && (
        <tr className="note-row">
          <td />
          <td colSpan={5}><div className="inner">⚠ {warning}</div></td>
        </tr>
      )}
    </>
  )
}

// ---- Anlegen ----------------------------------------------------------------------------

function CreateDoorCodeDialog({ groups, onClose, onCreated }: {
  groups: Group[]
  onClose: () => void
  onCreated: () => void
}) {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [label, setLabel] = useState('')
  const [endDate, setEndDate] = useState('')
  const [groupIDs, setGroupIDs] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<CreateDoorCodeResult | null>(null)
  const [busy, setBusy] = useState(false)

  const resolvedDoors = useMemo(() => {
    const doorIDs = new Set<string>()
    for (const group of groups) {
      if (groupIDs.has(group.id)) group.doorIDs.forEach(id => doorIDs.add(id))
    }
    return doorIDs.size
  }, [groups, groupIDs])

  const submit = async () => {
    setError(null)
    setBusy(true)
    try {
      const result = await api.post<CreateDoorCodeResult>('/doorcodes', {
        firstName, lastName,
        label: label || null,
        endDate: endDate ? new Date(endDate).toISOString() : null,
        groupScope: 'restricted',
        groups: [...groupIDs].map(id => ({ groupID: id, scheduleMode: 'inherit' })),
      })
      setCreated(result)
      onCreated()
    } catch (caught) {
      setError(describeError(caught))
    } finally {
      setBusy(false)
    }
  }

  if (created) {
    return (
      <Modal onClose={onClose} title={de.doorcodes.newCodeTitle} width={420}>
        <div style={{ textAlign: 'center', margin: 'var(--sp-4) 0' }}>
          {/* Einzige Stelle, an der ein Code offen gezeigt wird: er muss jetzt
              weitergegeben werden. Siehe design/11-offene-fragen.md F8. */}
          <div className="code-display">{created.doorcode.replace(/(\d{3})(\d{3})/, '$1 $2')}</div>
        </div>

        <div className="help" style={{ textAlign: 'center' }}>{de.doorcodes.newCodeHint}</div>

        {created.warnings.map((warning, index) => (
          <div key={index} style={{ marginTop: 'var(--sp-3)' }}>
            <Alert kind="warning">{warning.message}</Alert>
          </div>
        ))}

        <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end',
                      marginTop: 'var(--sp-4)' }}>
          <button className="btn btn-ghost"
                  onClick={() => void navigator.clipboard?.writeText(created.doorcode)}>
            ⧉ {de.app.copy}
          </button>
          <Link className="btn btn-primary" to={`/doorcodes/${created.doorCode.id}`}>
            {de.doorcodes.title}
          </Link>
        </div>
      </Modal>
    )
  }

  return (
    <Modal onClose={onClose} title={de.doorcodes.create} width={480}>
      {error && <div style={{ marginBottom: 'var(--sp-3)' }}><Alert kind="warning">{error}</Alert></div>}

      <div className="grid cols-2" style={{ gap: 'var(--sp-3)' }}>
        <Field label={de.doorcodes.firstName}>
          <input type="text" value={firstName} onChange={event => setFirstName(event.target.value)} />
        </Field>
        <Field label={de.doorcodes.lastName}>
          <input type="text" value={lastName} onChange={event => setLastName(event.target.value)} />
        </Field>
      </div>

      <Field label={de.doorcodes.label} help={de.doorcodes.labelHelp}>
        <input type="text" value={label} onChange={event => setLabel(event.target.value)} />
      </Field>

      <Field label={de.doorcodes.validUntil} help={de.doorcodes.unlimitedHint}>
        <input type="date" value={endDate} onChange={event => setEndDate(event.target.value)} />
      </Field>

      <Field label={de.doorcodes.filterGroup}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-1)' }}>
          {groups.map(group => (
            <label key={group.id} className="sm" style={{ display: 'flex', gap: 6 }}>
              <input type="checkbox" checked={groupIDs.has(group.id)}
                     onChange={event => {
                       setGroupIDs(current => {
                         const next = new Set(current)
                         if (event.target.checked) next.add(group.id)
                         else next.delete(group.id)
                         return next
                       })
                     }} />
              {group.name}
            </label>
          ))}
        </div>
      </Field>

      {groupIDs.size === 0
        ? <Alert kind="warning">{de.doorcodes.noGroupWarning}</Alert>
        : <Alert kind="info">{fmt(de.accounts.resolvedDoors, { doors: `${resolvedDoors} Türen` })}</Alert>}

      <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end',
                    marginTop: 'var(--sp-4)' }}>
        <button className="btn btn-ghost" onClick={onClose}>{de.app.cancel}</button>
        <button className="btn btn-primary" disabled={busy} onClick={() => void submit()}>
          {de.doorcodes.create}
        </button>
      </div>
    </Modal>
  )
}

function Modal({ title, width, children, onClose }: {
  title: string
  width: number
  children: React.ReactNode
  onClose: () => void
}) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgb(0 0 0 / .4)',
      display: 'grid', placeItems: 'center', zIndex: 60, overflow: 'auto', padding: 'var(--sp-5)',
    }} role="dialog" aria-modal="true" aria-label={title}
         onClick={event => { if (event.target === event.currentTarget) onClose() }}>
      <div className="card" style={{ width, boxShadow: 'var(--shadow-2)' }}>
        <div className="card-h">{title}</div>
        <div className="card-b">{children}</div>
      </div>
    </div>
  )
}

// ---- Detail -----------------------------------------------------------------------------

export function DoorCodeDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { toast, show, clear } = useToast()
  const [confirmDelete, setConfirmDelete] = useState(false)

  const code = useResource<DoorCodeDetail>(`/doorcodes/${id}`, [id])
  const access = useResource<EffectiveAccess>(`/doorcodes/${id}/effective-access`, [id])
  const history = useResource<CursorPage<AccessEvent>>(`/accessevents?doorCodeId=${id}&limit=5`, [id])
  const groups = useResource<Group[]>('/groups')

  if (code.loading) return <Spinner />
  if (code.error) return <ErrorBox error={code.error} onRetry={code.reload} />
  if (!code.data) return <ErrorBox error={new Error('not found')} />

  const detail = code.data

  const patch = async (body: Record<string, unknown>) => {
    try {
      await api.patch(`/doorcodes/${id}`, body)
      code.reload()
      access.reload()
      show(de.app.save)
    } catch (error) {
      show(describeError(error), 'warn')
    }
  }

  const regenerate = async () => {
    try {
      const result = await api.post<CreateDoorCodeResult>(`/doorcodes/${id}/regenerate`)
      code.reload()
      show(`${de.doorcodes.regenerate}: ${result.doorcode}`)
    } catch (error) {
      show(describeError(error), 'warn')
    }
  }

  const remove = async () => {
    try {
      await api.delete(`/doorcodes/${id}`)
      navigate('/doorcodes')
    } catch (error) {
      setConfirmDelete(false)
      show(describeError(error), 'warn')
    }
  }

  return (
    <>
      <PageHeader
        title={detail.displayName}
        back={{ to: '/doorcodes', label: de.doorcodes.title }}
        subtitle={detail.createdByName
          ? fmt(de.doorcodes.createdBy, { name: detail.createdByName })
          : undefined}
        actions={<>
          <button className="btn btn-ghost" onClick={() => void patch({ isActive: !detail.isActive })}>
            {detail.isActive ? de.doorcodes.deactivate : de.doorcodes.activate}
          </button>
          <button className="btn btn-danger" onClick={() => setConfirmDelete(true)}>
            {de.app.delete}
          </button>
        </>} />

      <div className="grid cols-2" style={{ marginBottom: 'var(--sp-4)' }}>
        <Card title="Stammdaten">
          <div className="grid cols-2" style={{ gap: 'var(--sp-3)' }}>
            <Field label={de.doorcodes.firstName}>
              <input type="text" defaultValue={detail.firstName ?? ''}
                     onBlur={event => void patch({ firstName: event.target.value })} />
            </Field>
            <Field label={de.doorcodes.lastName}>
              <input type="text" defaultValue={detail.lastName ?? ''}
                     onBlur={event => void patch({ lastName: event.target.value })} />
            </Field>
          </div>

          <Field label={de.doorcodes.label} help={de.doorcodes.labelHelp}>
            <input type="text" defaultValue={detail.label ?? ''}
                   onBlur={event => void patch({ label: event.target.value })} />
          </Field>

          <Field label={de.doorcodes.validUntil} help={de.doorcodes.unlimitedHint}>
            <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
              <input type="date"
                     defaultValue={detail.endDate ? detail.endDate.slice(0, 10) : ''}
                     onBlur={event => void patch(event.target.value
                       ? { endDate: new Date(event.target.value).toISOString() }
                       : { clearEndDate: true })} />
              {detail.endDate && (
                <button className="btn btn-ghost" onClick={() => void patch({ clearEndDate: true })}>
                  {de.app.unlimited}
                </button>
              )}
            </div>
          </Field>

          <Field label="Status">
            {detail.isActive
              ? <span className="status status-ok">● {de.app.active}</span>
              : <span className="status status-off">⊘ {de.app.inactive}</span>}
          </Field>
        </Card>

        <Card title={de.doorcodes.accessCode}>
          <div style={{ textAlign: 'center', margin: 'var(--sp-3) 0' }}>
            <MaskedCode code={detail.doorcode} big onCopy={() => show(de.app.copied)} />
          </div>

          <div className="field">
            <button className="btn btn-ghost" onClick={() => void regenerate()}>
              {de.doorcodes.regenerate}
            </button>
            <div className="help">{de.doorcodes.regenerateHint}</div>
          </div>

          <hr style={{ border: 0, borderTop: '1px solid var(--border)', margin: 'var(--sp-4) 0' }} />

          <div className="sm muted">{de.doorcodes.keyTag}</div>
          <div style={{ margin: 'var(--sp-1) 0' }}>
            {detail.keyTagAssigned
              ? <span className="mono">🔑 {detail.keyTagMasked}</span>
              : <span className="faint">{de.doorcodes.keyTagNone}</span>}
          </div>
          {/* Keine Schaltfläche: Schlüsseltags sind App-exklusiv. */}
          <div className="disabled-hint">ⓘ {de.doorcodes.keyTagAppOnly}</div>
        </Card>
      </div>

      <GroupAssignments detail={detail} groups={groups.data ?? []}
                        onChanged={() => { code.reload(); access.reload() }}
                        onError={message => show(message, 'warn')} />

      <div style={{ marginTop: 'var(--sp-4)' }}>
        <EffectiveAccessCard access={access.data} doorCodeID={id}
                             onError={message => show(message, 'warn')} />
      </div>

      <div style={{ marginTop: 'var(--sp-4)' }}>
        <Card title={de.doorcodes.history}
              actions={<Link className="btn btn-ghost" to={`/log?doorCodeId=${id}`}>
                {de.doorcodes.showAll}
              </Link>}>
          {history.data && history.data.items.length === 0 && (
            <div className="faint sm">{de.app.noEntries}</div>
          )}
          {history.data && history.data.items.length > 0 && (
            <table><tbody>
              {history.data.items.map(event => (
                <tr key={event.id}>
                  <td className="mono faint" style={{ width: 150 }}>{formatDateTime(event.occurredAt)}</td>
                  <td>{event.doorName}</td>
                  <td>
                    {event.granted
                      ? <span className="status status-ok">✓ {de.log.granted}</span>
                      : <span className="status" style={{ color: 'var(--danger)' }}>
                          ✗ {event.explanation ?? de.log.denied}
                        </span>}
                  </td>
                </tr>
              ))}
            </tbody></table>
          )}
        </Card>
      </div>

      {confirmDelete && (
        <ConfirmDialog
          title={fmt(de.doorcodes.confirmDelete, { name: detail.displayName })}
          detail={de.doorcodes.confirmDeleteDetail}
          confirmLabel={de.app.delete}
          onConfirm={() => void remove()}
          onCancel={() => setConfirmDelete(false)} />
      )}

      {toast && <Toast message={toast.message} kind={toast.kind} onDone={clear} />}
    </>
  )
}

function GroupAssignments({ detail, groups, onChanged, onError }: {
  detail: DoorCodeDetail
  groups: Group[]
  onChanged: () => void
  onError: (message: string) => void
}) {
  const [draft, setDraft] = useState<Record<string, ScheduleRule[]>>({})

  const save = async (assignments: Array<{
    groupID: string
    scheduleMode: 'inherit' | 'narrowed'
    rules?: ScheduleRule[]
  }>) => {
    try {
      await api.put(`/doorcodes/${detail.id}/groups`, { groups: assignments })
      setDraft({})
      onChanged()
    } catch (error) {
      onError(describeError(error))
    }
  }

  const current = detail.groupAssignments.map(assignment => ({
    groupID: assignment.groupID,
    scheduleMode: assignment.scheduleMode,
    rules: draft[assignment.groupID] ?? assignment.ownRules,
  }))

  const available = groups.filter(group =>
    !detail.groupAssignments.some(assignment => assignment.groupID === group.id))

  return (
    <Card title={de.doorcodes.groupsAndTimes} actions={
      available.length > 0 ? (
        <select style={{ width: 'auto' }} defaultValue=""
                onChange={event => {
                  if (!event.target.value) return
                  void save([...current, { groupID: event.target.value, scheduleMode: 'inherit' }])
                }}>
          <option value="">{de.doorcodes.addGroup}…</option>
          {available.map(group => <option key={group.id} value={group.id}>{group.name}</option>)}
        </select>
      ) : undefined
    }>
      {detail.groupAssignments.length === 0 && (
        <Alert kind="warning">{de.doorcodes.noGroupWarning}</Alert>
      )}

      {detail.hiddenGroupCount > 0 && (
        <div style={{ marginBottom: 'var(--sp-3)' }}>
          {/* Verschweigen würde den Eindruck erzeugen, der Zugang sei vollständig entzogen. */}
          <Alert kind="info">
            {fmt(de.doorcodes.hiddenGroups, { count: detail.hiddenGroupCount })}
          </Alert>
        </div>
      )}

      {detail.groupAssignments.map(assignment => {
        const narrowed = assignment.scheduleMode === 'narrowed'
        const rules = draft[assignment.groupID] ?? assignment.ownRules

        return (
          <div className="card" key={assignment.groupID}
               style={{ boxShadow: 'none', marginBottom: 'var(--sp-3)' }}>
            <div className="card-b">
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)',
                            marginBottom: 'var(--sp-2)' }}>
                <span className="chip" style={{ color: assignment.color }}>
                  <span>{assignment.groupName}</span>
                </span>
                <span className="sm muted">{assignment.doorIDs.length} Türen</span>
                <div className="spacer" />
                <button className="btn btn-ghost btn-icon"
                        aria-label={de.app.delete}
                        onClick={() => void save(current.filter(x => x.groupID !== assignment.groupID))}>
                  ×
                </button>
              </div>

              <div style={{ display: 'flex', gap: 'var(--sp-4)', marginBottom: 'var(--sp-2)' }}>
                <label className="sm" style={{ display: 'flex', gap: 6 }}>
                  <input type="radio" checked={!narrowed}
                         onChange={() => void save(current.map(x => x.groupID === assignment.groupID
                           ? { ...x, scheduleMode: 'inherit' as const }
                           : x))} />
                  {de.doorcodes.inheritSchedule}
                </label>
                <label className="sm" style={{ display: 'flex', gap: 6 }}>
                  <input type="radio" checked={narrowed}
                         onChange={() => {
                           // Verengen braucht Zeitfenster: ein leerer Plan ergäbe im Schnitt
                           // mit der Gruppe keinen Zugriff.
                           const seed = assignment.groupIsAlwaysOpen
                             ? [{ dayMask: 0b0011111, startMinute: 420, endMinute: 1140 }]
                             : assignment.groupRules
                           setDraft(value => ({ ...value, [assignment.groupID]: seed }))
                         }} />
                  {de.doorcodes.narrowSchedule}
                </label>
              </div>

              {!narrowed && !draft[assignment.groupID] && (
                <div className="sm muted">
                  {summarizeSchedule(assignment.groupRules, assignment.groupIsAlwaysOpen)}
                </div>
              )}

              {(narrowed || draft[assignment.groupID]) && (
                <>
                  <ScheduleGrid
                    rowHeight={16}
                    bands={[
                      {
                        rules: assignment.groupIsAlwaysOpen
                          ? [{ dayMask: ALL_DAYS, startMinute: 0, endMinute: 1440 }]
                          : assignment.groupRules,
                        color: 'var(--surface-2)',
                        ghost: true,
                        label: de.schedule.groupPlan,
                      },
                      { rules, color: assignment.color, label: de.schedule.effective },
                      ...(assignment.clippedRules.length > 0
                        ? [{ rules: assignment.clippedRules, color: 'var(--danger)',
                             clipped: true, label: de.schedule.clipped }]
                        : []),
                    ]} />

                  <ScheduleRuleList rules={rules}
                                    onChange={next => setDraft(value => ({
                                      ...value, [assignment.groupID]: next,
                                    }))} />

                  <button className="btn btn-primary" style={{ marginTop: 'var(--sp-2)' }}
                          onClick={() => void save(current.map(x => x.groupID === assignment.groupID
                            ? { ...x, scheduleMode: 'narrowed' as const, rules }
                            : x))}>
                    {de.app.save}
                  </button>
                </>
              )}
            </div>
          </div>
        )
      })}
    </Card>
  )
}

function EffectiveAccessCard({ access, doorCodeID, onError }: {
  access: EffectiveAccess | null
  doorCodeID: string
  onError: (message: string) => void
}) {
  const [doorID, setDoorID] = useState('')
  const [at, setAt] = useState(() => new Date().toISOString().slice(0, 16))
  const [probe, setProbe] = useState<ProbeResult | null>(null)

  const doors = access?.doors ?? []
  const selected = doors.find(door => door.doorID === doorID) ?? doors[0]

  const run = async () => {
    const target = doorID || selected?.doorID
    if (!target) return
    try {
      setProbe(await api.post<ProbeResult>(`/doorcodes/${doorCodeID}/probe`, {
        doorID: target,
        at: new Date(at).toISOString().slice(0, 19),
      }))
    } catch (error) {
      onError(describeError(error))
    }
  }

  return (
    <Card title={de.doorcodes.effectiveAccess} actions={
      doors.length > 0 ? (
        <select style={{ width: 'auto' }} value={selected?.doorID ?? ''}
                onChange={event => setDoorID(event.target.value)}>
          {doors.map(door => <option key={door.doorID} value={door.doorID}>{door.doorName}</option>)}
        </select>
      ) : undefined
    }>
      {doors.length === 0 && <div className="faint sm">{de.doorcodes.noAccessAtDoor}</div>}

      {selected && (
        <>
          <ScheduleGrid rowHeight={14} bands={[{
            rules: selected.isAlways
              ? [{ dayMask: ALL_DAYS, startMinute: 0, endMinute: 1440 }]
              : selected.rules,
            color: selected.fromGroups[0]?.color ?? 'var(--primary)',
            label: selected.fromGroups.map(group => group.name).join(', '),
          }]} />

          <div className="probe">
            <strong>{de.doorcodes.probe}:</strong>
            <input type="datetime-local" value={at} onChange={event => setAt(event.target.value)} />
            <button className="btn btn-ghost" onClick={() => void run()}>{de.doorcodes.probeRun}</button>

            {probe && (
              <span className={`probe-result ${probe.granted ? 'probe-yes' : 'probe-no'}`}>
                {probe.granted ? `✓ ${de.doorcodes.probeGranted}` : `✗ ${de.doorcodes.probeDenied}`}
                {' — '}{probe.explanation}
              </span>
            )}
          </div>

          <div className="help" style={{ marginTop: 'var(--sp-2)' }}>
            Die Probe ruft denselben Server-Endpunkt auf, der die Firmware-Payload baut.
          </div>
        </>
      )}
    </Card>
  )
}
